import { WorkflowTransition } from '../../components/home/workflows/WorkflowFlowChart';
import { SmartLabel } from './smart-label.model';

export interface CreatableWorkflow {
  workQueueUUID: string;
  name: string;
  description?: string;
  steps: WorkflowStep[];
}

export type DraftCreatableWorkflow = Omit<CreatableWorkflow, 'steps'>;

export interface EditableWorkflow extends CreatableWorkflow {
  workflowUUID: string;
}

export interface Workflow extends EditableWorkflow {
  state: WorkflowState;
  version: number;
  modifiedByUserID: string;
  modifiedDateTimeUTC: string;
  correlationUUID: string;
}

export type WorkflowStep = WorkflowStartStep | WorkflowIntermediateStep;

export interface WorkflowStartStep extends WorkflowStepBase {
  type: WorkflowStepType.Start;
  trigger: WorkflowTrigger;
}

export interface WorkflowIntermediateStep extends WorkflowStepBase {
  type: WorkflowStepType.Intermediate;
  trigger?: WorkflowTrigger;
}

export interface WorkflowStepBase {
  name: string;
  description: string;
  action: WorkflowActions;
  onSuccess?: WorkflowStepTransition;
  onFailure?: WorkflowStepTransition;
  onCancel?: WorkflowStepTransition;
}

export type WorkflowActions =
  | WorkflowSucceedAction
  | WorkflowFailAction
  | WorkflowCancelAction
  | WorkflowPublishAlertAction
  | WorkflowWaitAction
  | WorkflowRejectTripLegAction
  | WorkflowTaskHumanAction
  | WorkflowPublishEventAction
  | WorkflowConditionAction
  | WorkflowActivateWillCallAction
  | WorkflowCancelTripLegAction;

export interface WorkflowStepTransition {
  triggerStep?: string;
}

interface WorkflowTrigger {
  events: { name: string; version: number }[];
}

interface WorkflowSucceedAction {
  succeed: object;
}

interface WorkflowFailAction {
  fail: object;
}

export interface WorkflowCancelAction {
  cancel: object;
}

export interface WorkflowPublishAlertAction {
  publishAlert: WorkflowAlert;
}

export interface WorkflowWaitAction {
  wait: WorkflowWait;
}

interface WorkflowRejectTripLegAction {
  rejectTripLeg: WorkflowRejectTripLeg;
}

export interface WorkflowTaskHumanAction {
  taskHuman: WorkflowAlert;
}

export interface WorkflowPublishEventAction {
  publishEvent: WorkflowPublishEvent | WorkflowPublishEventTemplate;
}

export interface WorkflowConditionAction {
  condition: WorkflowCondition;
}

export interface WorkflowActivateWillCallAction {
  activateWillCall: object;
}

export interface WorkflowCancelTripLegAction {
  cancelTripLeg: WorkflowCancelTripLeg;
}

export interface WorkflowAlert {
  alertEventBC: string;
  alertDescription: string;
  alertTypeBC: string;
  alertRecipientBC?: string;
  interactiveResponse: boolean; // true if an interactive response is required, false otherwise
  responseTimeoutMinutes?: number; // number of minutes allowed for a response to be received (absent if interactiveResponse is false)
  sendReminderIfNoResponse?: boolean; // true if a reminder message is required, false otherwise (absent if interactiveResponse is false)
  numberOfReminders?: number; // number of reminders to send (absent if sendReminderIfNoResponse is false)
  sendReminderAfterMinutes?: number; // timing of reminder (absent if sendReminderIfNoResponse is false)
  alertReminder?: string; // reminder message (absent if sendReminderIfNoResponse is false)
  alertForSTOReservations?: boolean; // Flag to control if alerts are sent in the context of standing order reservation. If set to true, alerts will be sent for all reservations. If false or unset, alerts will only be sent for non-standing order reservations.
}

export interface WorkflowWait {
  baseDateTimeRef: WorkflowBaseDateTimeRef;
  deltaMinutes: number;
}

interface WorkflowRejectTripLeg {
  rejectTripLegReasonBC: string;
}

interface WorkflowCancelTripLeg {
  tripLegStatusReasonBCs: string[];
}

export interface WorkflowPublishEvent {
  name: string;
  detailSource: Exclude<WorkflowDetailSource, WorkflowDetailSource.Template>;
}

export interface WorkflowPublishEventTemplate {
  name: string;
  detailSource: WorkflowDetailSource.Template;
  detailTemplate: string | object;
}

export interface WorkflowCondition {
  inputPath: string;
  cases: WorkflowCaseClause[];
  elseCase?: WorkflowElseCase;
}

export interface WorkflowCaseClause {
  when: WorkflowWhenClause;
  outcomeStatus: WorkflowConditionOutcomeStatus;
}

export interface WorkflowElseCase {
  outcomeStatus: WorkflowConditionOutcomeStatus;
}

interface WorkflowWhenClause {
  op: ComparisonOperator;
  value: string;
}

export enum WorkflowConditionOutcomeStatus {
  Succeeded = 'succeeded',
  Failed = 'failed',
}

type ComparisonOperator = 'eq'; // Just eq for now

export enum WorkflowBaseDateTimeRef {
  TripLegAppointment = 'TripLegAppointment',
  TripLegPickup = 'TripLegPickup',
  EntityModified = 'EntityModified',
  CurrentTime = 'CurrentTime',
}

export enum WorkflowDetailSource {
  StepInput = 'StepInput',
  LastTriggerEvent = 'LastTriggerEvent',
  StartTriggerEvent = 'StartTriggerEvent',
  Template = 'Template',
}

export enum WorkflowStepType {
  Start = 'start',
  Intermediate = 'intermediate',
}

export enum WorkflowState {
  Draft = 'draft',
  Active = 'active',
  Inactive = 'inactive',
  Deleted = 'deleted',
  Archived = 'archived',
}

export enum WorkflowActionType {
  Succeed = 'succeed',
  Fail = 'fail',
  Cancel = 'cancel',
  PublishAlert = 'publishAlert',
  Wait = 'wait',
  RejectTripLeg = 'rejectTripLeg',
  TaskHuman = 'taskHuman',
  PublishEvent = 'publishEvent',
}

export enum WorkflowConditionalBC {
  Affirm = 'AFFIRM',
  Negate = 'NEGATE',
  Timeout = 'TIMEOUT',
}

export const WorkflowConditionals = [
  {
    name: 'Affirm',
    bc: WorkflowConditionalBC.Affirm,
  },
  {
    name: 'Negate',
    bc: WorkflowConditionalBC.Negate,
  },
  {
    name: 'Timeout',
    bc: WorkflowConditionalBC.Timeout,
  },
];
